import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './Icon';
import type { Workflow } from '../types';
import { listWorkflows, deleteWorkflow } from '../lib/storage';

interface Props {
  open: boolean;
  currentId: string;
  onClose: () => void;
  onSelect: (wf: Workflow) => void;
  onCreate: () => void;
  onDelete: (id: string) => void;
}

function timeAgo(ts: number): string {
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function WorkflowListMenu({ open, currentId, onClose, onSelect, onCreate, onDelete }: Props) {
  const [items, setItems] = useState<Workflow[]>([]);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setItems(listWorkflows().sort((a, b) => b.updatedAt - a.updatedAt));
      setQuery('');
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(w =>
      w.name.toLowerCase().includes(q) ||
      (w.description || '').toLowerCase().includes(q)
    );
  }, [items, query]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  function handleDelete(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    deleteWorkflow(id);
    setItems(items.filter(i => i.id !== id));
    onDelete(id);
  }

  return (
    <div ref={ref} className="wflist-menu" role="menu">
      <div className="wflist-head">
        <span>Your workflows</span>
        <span className="wflist-count">{items.length}</span>
      </div>

      <button className="wflist-new" onClick={() => { onCreate(); onClose(); }}>
        <span className="wflist-new-ic"><Icon name="plus" size={14} variant="gold" /></span>
        <b>New workflow</b>
        <span>Start with a blank canvas</span>
      </button>

      {items.length > 3 && (
        <div className="wflist-search">
          <Icon name="search" size={12} variant="silver" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search workflows…"
            autoFocus
          />
          {query && (
            <button className="wflist-search-clear" onClick={() => setQuery('')}>
              <Icon name="x" size={11} variant="muted" />
            </button>
          )}
        </div>
      )}

      <div className="wflist-items">
        {items.length === 0 && (
          <div className="wflist-empty">
            <Icon name="grid" size={20} variant="muted" />
            <span>No saved workflows yet.<br/>Build one and hit Save.</span>
          </div>
        )}
        {items.length > 0 && filtered.length === 0 && (
          <div className="wflist-empty">
            <Icon name="search" size={20} variant="muted" />
            <span>No workflows match "{query}".</span>
          </div>
        )}
        {filtered.map(wf => (
          <div
            key={wf.id}
            className={`wflist-item ${wf.id === currentId ? 'current' : ''}`}
            onClick={() => { onSelect(wf); onClose(); }}
            role="menuitem"
            tabIndex={0}
          >
            <span className={`wflist-dot ${wf.active ? 'on' : ''}`} title={wf.active ? 'Active' : 'Paused'} />
            <div className="wflist-info">
              <b>{wf.name}</b>
              <span>
                v{wf.version} · {timeAgo(wf.updatedAt)} · {wf.nodes.length} blocks
              </span>
            </div>
            <button
              className="wflist-del"
              onClick={e => handleDelete(e, wf.id)}
              title="Delete workflow"
              aria-label="Delete workflow"
            >
              <Icon name="trash" size={13} variant="red" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
